import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import EmailEditor, { type EditorRef } from 'react-email-editor'
import { Send, Save, Ban, BookmarkPlus } from 'lucide-react'
import { toast } from 'sonner'
import { Page, PageHeader, Card, btnPrimary, btnSecondary, DetailSkeleton } from '@/components/rm'
import { Field, Input, Modal } from '@/components/rm/form'
import { useMarketing, useCampaignContent, useAudienceCount, useCampaignStats, type Audience } from '@/hooks/use-marketing'
import { AudiencePicker } from '@/components/marketing/audience-picker'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_app/marketing_/$id')({ component: CampaignEditor })

function CampaignEditor() {
  const { id } = Route.useParams()
  const { campaigns, usage, isLoading, save, schedule, cancel, sendTest, saveAsTemplate } = useMarketing()
  const editor = useRef<EditorRef>(null)

  const campaign = campaigns.find((c) => c.id === id)
  // The body is fetched separately: the campaign list carries names and status only.
  const { data: content } = useCampaignContent(id)
  const [name, setName] = useState('')
  const [subject, setSubject] = useState('')
  const [audience, setAudience] = useState<Audience>({ all: true })
  const [seeded, setSeeded] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)

  const { data: reach = 0 } = useAudienceCount(audience)
  const { data: stats } = useCampaignStats(campaign && campaign.status !== 'draft' ? id : null)
  const editable = campaign?.status === 'draft'

  // Seed once, from the loaded campaign. Re-seeding on every render would fight the user's typing —
  // the same trap that blanked the job form's draft autosave.
  useEffect(() => {
    if (!campaign || seeded) return
    setName(campaign.name)
    setSubject(campaign.subject)
    setAudience(campaign.audience ?? { all: true })
    setSeeded(true)
  }, [campaign, seeded])

  // Load once the design has actually arrived — the editor can become ready before the fetch
  // resolves, and loading nothing leaves the trade person staring at a blank canvas.
  const [editorReady, setEditorReady] = useState(false)
  useEffect(() => {
    if (editorReady && content?.design) editor.current?.editor?.loadDesign(content.design as never)
  }, [editorReady, content?.design])

  /** Pull design + html out of the editor iframe. Both are needed: design to re-edit, html to send. */
  const exportHtml = () =>
    new Promise<{ design: unknown; html: string }>((resolve, reject) => {
      const ed = editor.current?.editor
      if (!ed) return reject(new Error('The editor is still loading'))
      ed.exportHtml((data: { design: unknown; html: string }) => resolve(data))
    })

  const persist = async () => {
    const { design, html } = await exportHtml()
    await save.mutateAsync({ id, name, subject, design, html, audience })
  }

  const onSave = async () => {
    setBusy(true)
    try { await persist(); toast.success('Saved') }
    catch (e) { toast.error(e instanceof Error ? e.message : 'Could not save') }
    finally { setBusy(false) }
  }

  const onTest = async () => {
    setBusy(true)
    try {
      // Save first: testing wording you have not saved tests the wrong wording.
      await persist()
      const to = await sendTest.mutateAsync(id)
      toast.success(`Test sent to ${to}`)
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Could not send the test') }
    finally { setBusy(false) }
  }

  // Reuse without rebuilding: saving the design keeps the layout and wording, not the audience or
  // the send history.
  const onSaveTemplate = async () => {
    setBusy(true)
    try {
      const { design } = await exportHtml()
      await saveAsTemplate.mutateAsync({ name: name || 'My template', subject, design })
      toast.success('Saved to your templates — it will appear next time you start a campaign')
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Could not save the template') }
    finally { setBusy(false) }
  }

  const onSend = async () => {
    setBusy(true)
    try {
      await persist()
      const r = await schedule.mutateAsync({ id })
      setConfirming(false)
      toast.success(`Sending to ${r.recipients.toLocaleString()} ${r.recipients === 1 ? 'person' : 'people'}`)
    } catch (e) {
      // The database refuses over-budget or empty audiences with a sentence meant for the trade person.
      toast.error(e instanceof Error ? e.message : 'Could not start the campaign')
    } finally { setBusy(false) }
  }

  if (isLoading && !campaign) return <DetailSkeleton />
  if (!campaign) return <Page><PageHeader title="Campaign" /><Card className="mt-5 p-6"><p className="text-[14px] text-token-secondary">This campaign no longer exists.</p></Card></Page>

  const short = usage && reach > usage.sendable

  return (
    <Page>
      <PageHeader
        eyebrow="Marketing"
        title={editable ? 'Edit campaign' : campaign.name}
        subtitle={editable ? 'Design it, send yourself a test, then send it to your clients.' : `${campaign.status === 'sent' ? 'Sent' : 'Sending'} · ${campaign.subject}`}
        actions={editable ? (
        <>
          <button className={btnSecondary} onClick={onTest} disabled={busy}>Send me a test</button>
          <button className={btnSecondary} onClick={onSaveTemplate} disabled={busy} title="Save this design to reuse later">
            <BookmarkPlus className="h-4 w-4" /> Save as template
          </button>
          <button className={btnSecondary} onClick={onSave} disabled={busy}><Save className="h-4 w-4" /> Save</button>
          <button className={btnPrimary} onClick={() => setConfirming(true)} disabled={busy || reach === 0}>
            <Send className="h-4 w-4" /> Send to {reach.toLocaleString()}
          </button>
        </>
        ) : campaign.status === 'sending' ? (
          <button className={btnSecondary} onClick={() => cancel.mutate(id)}><Ban className="h-4 w-4" /> Stop sending</button>
        ) : undefined}
      />

      {!editable && stats && (
        <Card className="mt-5 mb-5 p-5">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
            {([['Sent', stats.sent, '#2faa6b'], ['Still to go', stats.pending, '#667085'],
               ['Unsubscribed', stats.suppressed, '#cf7a1a'], ['Skipped', stats.skipped, '#8a94a6'],
               ['Failed', stats.failed, '#e2566c']] as const).map(([label, n, colour]) => (
              <div key={label}>
                <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-token-muted">{label}</p>
                <p className="font-display text-[24px] font-bold tabular-nums" style={{ color: colour }}>{n.toLocaleString()}</p>
              </div>
            ))}
          </div>
          {stats.pending > 0 && (
            <p className="mt-3 text-[12.5px] text-token-secondary">
              Sending steadily rather than all at once, so it lands well and stays out of spam folders.
            </p>
          )}
        </Card>
      )}

      {editable && (
        <div className="mt-5 mb-5 grid gap-5 lg:grid-cols-3">
          <Card className="p-5 lg:col-span-2">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Campaign name"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="August newsletter" /></Field>
              <Field label="Subject line"><Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="What's new at {{company_name}}" /></Field>
            </div>
            <p className="mt-2 text-[12px] text-token-secondary">
              Use <code>{'{{first_name}}'}</code>, <code>{'{{company_name}}'}</code>, <code>{'{{company_phone}}'}</code> and
              {' '}<code>{'{{booking_url}}'}</code> anywhere — they're filled in for each person as it sends.
            </p>
          </Card>

          <Card className="p-5">
            <AudiencePicker audience={audience} onChange={setAudience} reach={reach} />
            {usage && (
              <p className={cn('mt-1 text-[12px]', short ? 'font-semibold text-token-danger' : 'text-token-secondary')}>
                {short
                  ? `You have ${usage.sendable.toLocaleString()} emails left — buy more or narrow the audience.`
                  : `${usage.sendable.toLocaleString()} emails left this month`}
              </p>
            )}
          </Card>
        </div>
      )}

      <Card className="overflow-hidden p-0">
        {editable ? (
          <EmailEditor ref={editor} onReady={() => setEditorReady(true)} minHeight="720px"
            options={{ appearance: { theme: 'light' } }} />
        ) : (
          // Sent campaigns show exactly what went out, frozen — not a re-render that might differ.
          <iframe title="Campaign" srcDoc={content?.html ?? ''} className="h-[720px] w-full border-0" />
        )}
      </Card>

      <Modal open={confirming} onClose={() => setConfirming(false)} title="Send this campaign?"
        footer={<>
          <button className={btnSecondary} onClick={() => setConfirming(false)}>Not yet</button>
          <button className={btnPrimary} onClick={onSend} disabled={busy}>{busy ? 'Starting…' : `Send to ${reach.toLocaleString()}`}</button>
        </>}>
        <div className="space-y-3 text-[13.5px] text-token-secondary">
          <p>This goes to <b className="text-token-primary">{reach.toLocaleString()}</b> {reach === 1 ? 'client' : 'clients'} and uses <b className="text-token-primary">{reach.toLocaleString()}</b> of your {usage?.sendable.toLocaleString() ?? 0} remaining emails.</p>
          <p>Anyone who has unsubscribed is already excluded, and is checked again as each email goes out.</p>
          <p className="text-token-secondary">Send yourself a test first if you haven't — you can't unsend a campaign.</p>
        </div>
      </Modal>
    </Page>
  )
}
